import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Turnstile } from '@marsidev/react-turnstile'
import { supabase } from '../lib/supabase'
import { useToast } from '../components/Toast'
import SEO from '../components/SEO'
import Logo from '../components/Logo'

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

export default function ForgotPassword() {
  const [email, setEmail]   = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent]     = useState(false)
  // Required because Supabase CAPTCHA protection is enabled at the
  // project level. resetPasswordForEmail rejects calls without a token.
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaRef = useRef(null)
  const toast = useToast()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!captchaToken) { toast('Please wait a moment for the security check to complete', 'error'); return }
    setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      captchaToken,
    })
    setLoading(false)
    if (error) {
      captchaRef.current?.reset()
      setCaptchaToken('')
      toast(error.message, 'error')
    } else {
      setSent(true)
    }
  }

  if (sent) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <SEO title="Check your email" noindex />
      <div className="fade-up" style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, background: 'var(--green)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', margin: '0 auto 24px', boxShadow: '0 8px 24px rgba(34,160,107,0.25)' }}>✉️</div>
        <h2 style={{ marginBottom: 12 }}>Check your email</h2>
        <p style={{ color: 'var(--ink-muted)', lineHeight: 1.7, marginBottom: 24 }}>
          We have sent a password reset link to <strong>{email}</strong>. Click the link to choose a new password.
        </p>
        <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
          <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 500 }}>Back to sign in</Link>
        </p>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <SEO title="Reset your password" noindex />
      <div style={{ position: 'fixed', top: 0, right: 0, width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(72,146,145,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div className="fade-up" style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Logo variant="icon" size={48} style={{ margin: '0 auto 16px' }} />
          <h1 style={{ fontSize: '2rem', marginBottom: 6 }}>Reset your password</h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>
            Enter your email and we will send you a reset link.
          </p>
        </div>

        <div className="card" style={{ padding: 32 }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading || !captchaToken}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              {loading ? 'Sending…' : 'Send reset link →'}
            </button>
            {TURNSTILE_SITE_KEY ? (
              <div style={{ marginTop: 16 }}>
                <Turnstile
                  ref={captchaRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={token => setCaptchaToken(token)}
                  onError={() => setCaptchaToken('')}
                  onExpire={() => setCaptchaToken('')}
                  options={{ theme: 'light', size: 'flexible' }}
                />
              </div>
            ) : null}
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.88rem', color: 'var(--ink-muted)' }}>
          <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 500 }}>Back to sign in</Link>
        </p>
      </div>
    </div>
  )
}
